import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const ProcessVisualization = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % 5);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const processSteps = [
    {
      id: 1,
      title: 'Discovery & Analysis',
      description: 'Deep dive into your business challenges, goals, and current state through collaborative workshops and comprehensive analysis.',
      icon: 'Search',
      color: 'from-blue-500 to-blue-600',
      duration: '1-2 weeks',
      deliverables: ['Stakeholder interviews', 'Current state assessment', 'Gap analysis', 'Opportunity mapping']
    },
    {
      id: 2,
      title: 'Strategy & Planning',
      description: 'Develop tailored strategies and detailed roadmaps that align with your vision and business objectives.',
      icon: 'Target',
      color: 'from-purple-500 to-purple-600',
      duration: '2-3 weeks',
      deliverables: ['Strategic framework', 'Implementation roadmap', 'Resource planning', 'Risk assessment']
    },
    {
      id: 3,
      title: 'Design & Prototyping',
      description: 'Create innovative solutions through iterative design processes, ensuring optimal user experience and functionality.',
      icon: 'Palette',
      color: 'from-orange-500 to-red-500',
      duration: '3-4 weeks',
      deliverables: ['Design concepts', 'Prototypes', 'User testing', 'Design systems']
    },
    {
      id: 4,
      title: 'Implementation & Execution',
      description: 'Execute the strategy with precision, maintaining quality standards while ensuring timely delivery and stakeholder alignment.',
      icon: 'Cog',
      color: 'from-green-500 to-emerald-600',
      duration: '4-12 weeks',
      deliverables: ['Solution development', 'Quality assurance', 'Training programs', 'Change management']
    },
    {
      id: 5,
      title: 'Optimization & Support',
      description: 'Continuous monitoring, optimization, and support to ensure sustained success and maximum return on investment.',
      icon: 'TrendingUp',
      color: 'from-indigo-500 to-blue-600',
      duration: 'Ongoing',
      deliverables: ['Performance monitoring', 'Optimization recommendations', 'Support documentation', 'Success metrics']
    }
  ];

  return (
    <div className="py-20 bg-gradient-to-b from-brand-navy via-primary to-secondary relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 opacity-15">
        <div className="absolute top-20 left-20 w-96 h-96 bg-brand-green/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-brand-orange/20 rounded-full blur-3xl animate-pulse" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-heading">
            How We
            <span className="holographic"> Partner</span>
          </h2>
          <p className="text-xl text-white/90 max-w-3xl mx-auto font-body">
            Our collaborative engineering methodology ensures every project becomes a testament to partnership, innovation, and sustainable impact.
          </p>
        </div>

        {/* Process Flow */}
        <div className="relative">
          {/* Connection Lines */}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-brand-cyan/30 via-brand-purple/30 via-brand-orange/30 via-brand-green/30 to-brand-blue/30 transform -translate-y-1/2"></div>
          
          {/* Process Steps */}
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-8">
            {processSteps.map((step, index) => (
              <div
                key={step.id}
                className={`relative transition-all duration-700 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${index * 200}ms` }}
                onMouseEnter={() => setActiveStep(index)}
              >
                {/* Step Card */}
                <div className={`relative glass-morph bg-primary/20 backdrop-blur-md rounded-2xl p-6 border transition-all duration-500 ${
                  activeStep === index ? 'border-brand-cyan/50 scale-105' : 'border-white/10'
                }`}>
                  {/* Step Number */}
                  <div className="absolute -top-4 left-6">
                    <div className={`w-8 h-8 rounded-full bg-gradient-to-r ${step.color} flex items-center justify-center text-white font-bold text-sm`}>
                      {step.id}
                    </div>
                  </div>

                  {/* Icon */}
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-r ${step.color} flex items-center justify-center mb-4 mx-auto`}>
                    <Icon name={step.icon} size={32} className="text-white" />
                  </div>

                  {/* Content */}
                  <div className="text-center">
                    <h3 className="text-lg font-bold text-white mb-2 font-heading">{step.title}</h3>
                    <p className="text-sm text-white mb-4 leading-relaxed font-body">{step.description}</p>

                    {/* Duration */}
                    <div className="flex items-center justify-center mb-4">
                      <Icon name="Clock" size={16} className="text-brand-cyan mr-2" />
                      <span className="text-sm font-medium text-white/90 font-body">{step.duration}</span>
                    </div>

                    {/* Deliverables - Show on active */}
                    <div className={`transition-all duration-500 ${
                      activeStep === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                    } overflow-hidden`}>
                      <div className="border-t border-white/20 pt-4">
                        <h4 className="text-sm font-semibold text-brand-cyan mb-2 font-heading">Key Deliverables:</h4>
                        <div className="space-y-1">
                          {step.deliverables.map((deliverable, delIndex) => (
                            <div key={delIndex} className="flex items-center text-xs text-white font-body">
                              <Icon name="CheckCircle" size={12} className="mr-2 text-brand-green flex-shrink-0" />
                              {deliverable}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Connection Arrow */}
                {index < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <Icon name="ArrowRight" size={24} className="text-brand-cyan/50" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Partnership Philosophy */}
        <div className={`mt-20 text-center transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '1000ms' }}>
          <div className="glass-morph bg-secondary/30 backdrop-blur-md rounded-2xl p-8 md:p-12 border border-white/10">
            <h3 className="text-3xl font-bold text-white mb-6 font-heading">
              Partnership Over Vendor Relationships
            </h3>
            <p className="text-lg text-white/90 max-w-4xl mx-auto mb-8 font-body">
              We don't just deliver projects; we become an extension of your team. Our collaborative engineering approach ensures knowledge transfer, capability building, and sustainable success that continues long after project completion.
            </p>

            {/* Partnership Principles */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="glass-morph bg-primary/20 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <Icon name="Users" size={32} className="text-brand-cyan mx-auto mb-4" />
                <h4 className="font-bold text-white mb-2 font-heading">Collaborative Teams</h4>
                <p className="text-sm text-white font-body">Integrated engineering teams working as one unified force</p>
              </div>
              <div className="glass-morph bg-primary/20 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <Icon name="BookOpen" size={32} className="text-brand-purple mx-auto mb-4" />
                <h4 className="font-bold text-white mb-2 font-heading">Knowledge Transfer</h4>
                <p className="text-sm text-white font-body">Building internal technical capabilities for long-term success</p>
              </div>
              <div className="glass-morph bg-primary/20 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <Icon name="Target" size={32} className="text-brand-green mx-auto mb-4" />
                <h4 className="font-bold text-white mb-2 font-heading">Shared Success</h4>
                <p className="text-sm text-white font-body">Aligned incentives and mutual investment in sustainable outcomes</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProcessVisualization;